
import { jwtDecode, InvalidTokenError } from 'jwt-decode';

// Safe token validation without throwing
export const decodeToken = (token) => {
  try {
    console.log('🔐 [AUTH UTILS] decodeToken called');
    console.log('📏 Input token length:', token?.length);
    console.log('🔍 Input token type:', typeof token);
    
    if (!token) {
      console.warn('❌ [AUTH UTILS] No token provided');
      return null;
    }
    
    if (typeof token !== 'string') {
      console.warn(`❌ [AUTH UTILS] Token is not a string: ${typeof token}`);
      return null;
    }
    
    const trimmedToken = token.trim();
    
    // Check for "undefined" string
    if (trimmedToken === 'undefined') {
      console.warn('❌ [AUTH UTILS] Token is literal string "undefined"');
      return null;
    }
    
    // Basic JWT format check
    if (trimmedToken.length < 50) {
      console.warn(`❌ [AUTH UTILS] Token too short for JWT: ${trimmedToken.length} chars`);
      console.warn(`   Token: "${trimmedToken}"`);
      return null;
    }
    
    const parts = trimmedToken.split('.');
    console.log(`🔍 [AUTH UTILS] Token has ${parts.length} parts`);
    
    if (parts.length !== 3) {
      console.warn(`❌ [AUTH UTILS] JWT should have 3 parts, got: ${parts.length}`);
      return null;
    }
    
    // Check each part
    for (let i = 0; i < parts.length; i++) {
      if (!parts[i] || parts[i].length === 0) {
        console.warn(`❌ [AUTH UTILS] JWT part ${i} is empty`);
        return null;
      }
    }
    
    console.log('✅ [AUTH UTILS] Token format looks good, decoding...');
    const decoded = jwtDecode(trimmedToken);
    console.log('✅ [AUTH UTILS] Token decoded successfully');
    console.log('📋 Decoded:', decoded);
    
    return decoded;
  } catch (error) {
    if (error instanceof InvalidTokenError) {
      console.error('❌ [AUTH UTILS] JWT decode failed:', error.message);
    } else {
      console.error('❌ [AUTH UTILS] Unexpected decode error:', error);
    }
    return null;
  }
};

export const isTokenExpired = (token) => {
  console.log('⏰ [AUTH UTILS] Checking token expiration');
  const decoded = decodeToken(token);
  
  if (!decoded || !decoded.exp) {
    console.warn('❌ [AUTH UTILS] Token invalid or has no expiration');
    return true;
  }
  
  const currentTime = Date.now() / 1000;
  const isExpired = decoded.exp < currentTime;
  
  console.log(`⏰ [AUTH UTILS] Token expires at: ${new Date(decoded.exp * 1000).toISOString()}`);
  console.log(`⏰ [AUTH UTILS] Current time: ${new Date(currentTime * 1000).toISOString()}`);
  console.log(`⏰ [AUTH UTILS] Is expired: ${isExpired}`);
  
  return isExpired;
};

export const getTokenData = () => {
  try {
    const token = localStorage.getItem('token');
    console.log('📝 [AUTH UTILS] getTokenData from localStorage');
    console.log('🔍 Token in storage:', token);
    console.log('📏 Length:', token?.length);
    
    if (!token) {
      console.log('📝 [AUTH UTILS] No token in storage');
      return null;
    }
    
    return decodeToken(token);
  } catch (error) {
    console.error('❌ [AUTH UTILS] Error getting token data:', error);
    return null;
  }
};

export const hasRole = (requiredRole) => {
  const tokenData = getTokenData();
  if (!tokenData) return false;
  
  return tokenData.role === requiredRole;
};

export const isValidTokenFormat = (token) => {
  console.log('✅ [AUTH UTILS] Validating token format');
  console.log('📏 Input length:', token?.length);
  console.log('🔍 Input type:', typeof token);
  
  if (!token || typeof token !== 'string') {
    console.log('❌ [AUTH UTILS] Not a valid string');
    return false;
  }
  
  const trimmedToken = token.trim();
  
  // Check for "undefined" string
  if (trimmedToken === 'undefined') {
    console.log('❌ [AUTH UTILS] Token is string "undefined"');
    return false;
  }
  
  // Basic length check
  if (trimmedToken.length < 50) {
    console.log(`❌ [AUTH UTILS] Token too short: ${trimmedToken.length} chars`);
    console.log(`   Token: "${trimmedToken}"`);
    return false;
  }
  
  const parts = trimmedToken.split('.');
  console.log(`🔍 [AUTH UTILS] Token parts: ${parts.length}`);
  
  if (parts.length !== 3) {
    console.log(`❌ [AUTH UTILS] Wrong part count: ${parts.length}`);
    return false;
  }
  
  // Check all parts exist
  const valid = parts[0] && parts[1] && parts[2];
  console.log(`✅ [AUTH UTILS] Token format valid: ${valid}`);
  
  return valid;
};

export const getTokenType = () => {
  const tokenData = getTokenData();
  if (!tokenData) return null;
  
  return tokenData.role;
};

export const isTempToken = () => {
  const tokenData = getTokenData();
  if (!tokenData) return false;
  
  return tokenData.role === 'business_temp' && tokenData.purpose === 'change_password';
};

// REMOVE the setToken, getToken, clearTokens you added previously
// REPLACE with these:

export const setToken = (token, role) => {
  sessionStorage.setItem('token', token);   // per-tab, never shared
  sessionStorage.setItem('active_role', role);
};

export const getToken = () => {
  return sessionStorage.getItem('token');   // reads only THIS tab's token
};

export const clearTokens = () => {
  sessionStorage.clear();                   // only clears THIS tab
  localStorage.removeItem('token');         // remove old key if still there
  localStorage.removeItem('token_admin');   // remove old role-based keys
  localStorage.removeItem('token_business');
  localStorage.removeItem('active_role');
  localStorage.removeItem('authType');
  localStorage.removeItem('industry');
};